import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { ProjectParameter } from '@deltek/specification-client/core/Models/projectParameter'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Backdrop from '@material-ui/core/Backdrop/Backdrop'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { AllProjectResponse } from 'Models/project'
import { GetProjectQuery } from 'api/graphQL/queries/project.queries'
import { isContentTeam } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import React, { useCallback } from 'react'
import {
    availableProjectParamters,
    getSelectedProjectParameterValue,
} from 'shared/utils/projectParameterUtils'
import ProjectParameterTextField from './ProjectParameterTextField'
import WorkspaceActionBarButtonWidget from './WorkspaceActionBarButtonWidget'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
    },
    dialogCont: {
        width: 630,
        '@media (max-width: 695px)': {
            width: 250,
        },
    },
    closeDialogButton: {
        color: theme.palette.secondary.main,
        fontSize: pxToRem(18),
        fontWeight: 400,
        '&.MuiButton-text': {
            padding: `${theme.spacing(0.75)}px ${theme.spacing(3)}px`,
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    gridCont: {
        display: 'flex',
        justifyContent: 'space-between',
    },
}))

interface ProjectParametersDialogProps {
    projectId?: string
}
const ProjectParametersDialog: React.FC<ProjectParametersDialogProps> = ({
    projectId = '',
}) => {
    const classes = useStyles()
    const [open, setOpen] = React.useState(false)
    const [projectParameters, setProjectParameters] = React.useState<
        Partial<ProjectParameter>[]
    >([])

    const isContentTeamUser = useReactiveVar(isContentTeam)

    const [
        getProject,
        { data: projectData, loading: projectLoading },
    ] = useLazyQuery<AllProjectResponse>(GetProjectQuery)

    const triggerProjectReload = useCallback(() => {
        getProject({
            variables: {
                projectId: projectId ?? '',
            },
        })
    }, [getProject, projectId])

    React.useEffect(() => {
        if (projectData && !projectLoading) {
            setProjectParameters(projectData.getProject.parameters ?? [])
        }
    }, [projectData, projectLoading])

    const handleClickOpen = () => {
        triggerProjectReload()
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <>
            <WorkspaceActionBarButtonWidget
                className="viewProjectParameters"
                onClick={handleClickOpen}
                disabled={isContentTeamUser}
            >
                View
            </WorkspaceActionBarButtonWidget>
            {!projectLoading ? (
                <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="view-project-parameters"
                >
                    <DialogTitle className={classes.dialogTitle}>
                        Project Parameters
                    </DialogTitle>
                    <DialogContent className={classes.dialogCont}>
                        <GridContainer
                            direction="row"
                            className={classes.gridCont}
                        >
                            {availableProjectParamters.map(
                                (parameter, index) => {
                                    return (
                                        <ProjectParameterTextField
                                            key={`${index}-${parameter?.name?.trim()}`}
                                            projectParameterValue={getSelectedProjectParameterValue(
                                                projectParameters,
                                                parameter.name
                                            )}
                                            projectParameterName={
                                                parameter.label
                                            }
                                            projectParameterId={parameter.name.replace(
                                                ' ',
                                                ''
                                            )}
                                        />
                                    )
                                }
                            )}
                        </GridContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            data-testid="close-dialog-button"
                            onClick={handleClose}
                            className={clsx(
                                classes.closeDialogButton,
                                'close-dialog-button'
                            )}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <Backdrop className={classes.backdrop} open={projectLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </>
    )
}

export default ProjectParametersDialog
